import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, RefreshCw, Download, Upload, PlayCircle, Database } from 'lucide-react';

interface Template {
  id: string;
  template_key: string;
  template_type: 'static' | 'parametrized';
  category: string;
  default_value: string;
  parameters: string[];
  description: string;
}

interface TemplateValue {
  template_key: string;
  language: string;
  value: string;
}

const SUPPORTED_LANGUAGES = [
  'en', 'es', 'fr', 'de', 'it', 'pt',
  'da', 'no', 'fi', 'sv', 'is',
  'cs', 'sk', 'sl', 'pl',
  'et', 'lv', 'lt',
  'el', 'nl',
  'ko', 'ja', 'zh',
  'ar', 'hi', 'ru', 'tr', 'id',
  'ro', 'bg', 'hr', 'hu', 'uk', 'he',
  'th', 'vi', 'ms', 'tl', 'fa', 'bn', 'ur', 'sw'
];

const TranslationTemplatesManager = () => {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateValues, setTemplateValues] = useState<Record<string, TemplateValue[]>>({});
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);

      // Load templates
      const { data: templatesData, error: templatesError } = await supabase
        .from('translation_templates')
        .select('*')
        .order('category', { ascending: true })
        .order('template_key', { ascending: true });

      if (templatesError) throw templatesError;
      setTemplates((templatesData || []) as Template[]);

      // Load template values
      const { data: valuesData, error: valuesError } = await supabase
        .from('translation_template_values')
        .select('*');

      if (valuesError) throw valuesError;

      // Group values by template_key
      const grouped: Record<string, TemplateValue[]> = {};
      (valuesData || []).forEach((value: TemplateValue) => {
        if (!grouped[value.template_key]) {
          grouped[value.template_key] = [];
        }
        grouped[value.template_key].push(value);
      });
      setTemplateValues(grouped);

    } catch (error: any) {
      console.error('Error loading templates:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSeedTemplates = async () => {
    try {
      setSeeding(true);
      toast({
        title: 'Sembrando plantillas...',
        description: 'Esto puede tardar unos segundos',
      });

      const { data, error } = await supabase.functions.invoke('seed-translation-templates');

      if (error) throw error;

      toast({
        title: 'Plantillas sembradas',
        description: `${data.templatesInserted} plantillas creadas con ${data.valuesInserted} valores en inglés`,
      });

      await loadTemplates();

    } catch (error: any) {
      console.error('Error seeding templates:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSeeding(false);
    }
  };

  const handleTranslateTemplates = async () => {
    try {
      setTranslating(true);
      toast({
        title: 'Traduciendo plantillas...',
        description: `Traduciendo a ${SUPPORTED_LANGUAGES.length - 1} idiomas. Esto puede tardar varios minutos.`,
      });

      const { data, error } = await supabase.functions.invoke('translate-templates', {
        body: { targetLanguages: SUPPORTED_LANGUAGES }
      });

      if (error) throw error;

      toast({
        title: 'Traducciones completadas',
        description: `${data.totalTranslated} traducciones generadas exitosamente`,
      });

      await loadTemplates();

    } catch (error: any) {
      console.error('Error translating templates:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setTranslating(false);
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.template_key.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          template.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['all', ...Array.from(new Set(templates.map(t => t.category)))];

  const stats = {
    totalTemplates: templates.length,
    staticTemplates: templates.filter(t => t.template_type === 'static').length,
    parametrizedTemplates: templates.filter(t => t.template_type === 'parametrized').length,
    totalTranslations: Object.values(templateValues).reduce((sum, values) => sum + values.length, 0),
    languagesCovered: new Set(Object.values(templateValues).flat().map(v => v.language)).size
  };

  return (
    <>
      <Helmet>
        <title>Translation Templates Manager - Dev Tools</title>
        <meta name="description" content="Manage translation templates for optimized multilingual content" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Translation Templates Manager</h1>
            <p className="text-muted-foreground">
              Optimize translations by managing reusable templates (reduces AI calls by 99.996%)
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Total Templates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalTemplates}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Static</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{stats.staticTemplates}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Parametrized</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">{stats.parametrizedTemplates}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Total Translations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.totalTranslations}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Languages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{stats.languagesCovered}</div>
              </CardContent>
            </Card>
          </div>

          {/* Actions */}
          <div className="flex gap-4 mb-8">
            <Button
              onClick={handleSeedTemplates}
              disabled={seeding}
              variant="outline"
            >
              {seeding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sembrando...
                </>
              ) : (
                <>
                  <Database className="mr-2 h-4 w-4" />
                  Sembrar Plantillas
                </>
              )}
            </Button>

            <Button
              onClick={handleTranslateTemplates}
              disabled={translating || templates.length === 0}
            >
              {translating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Traduciendo...
                </>
              ) : (
                <>
                  <PlayCircle className="mr-2 h-4 w-4" />
                  Traducir Todo
                </>
              )}
            </Button>

            <Button
              onClick={loadTemplates}
              variant="outline"
              disabled={loading}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Recargar
            </Button>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <Input
              placeholder="Buscar plantillas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
            <div className="flex gap-2 flex-wrap">
              {categories.map(category => (
                <Badge
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Badge>
              ))}
            </div>
          </div>

          {/* Templates List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTemplates.map(template => (
                <Card key={template.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg flex items-center gap-2">
                          {template.template_key}
                          <Badge variant={template.template_type === 'static' ? 'default' : 'secondary'}>
                            {template.template_type}
                          </Badge>
                          <Badge variant="outline">{template.category}</Badge>
                        </CardTitle>
                        <CardDescription className="mt-1">
                          {template.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm font-medium">Default (EN):</span>
                        <p className="text-sm text-muted-foreground mt-1">{template.default_value}</p>
                      </div>
                      {template.parameters.length > 0 && (
                        <div>
                          <span className="text-sm font-medium">Parameters:</span>
                          <div className="flex gap-2 mt-1">
                            {template.parameters.map(param => (
                              <Badge key={param} variant="outline">{`{${param}}`}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        <span className="text-sm font-medium">Translations:</span>
                        <div className="text-sm text-muted-foreground mt-1">
                          {templateValues[template.template_key]?.length || 0} languages
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TranslationTemplatesManager;